import { useState, useMemo, useEffect, useRef } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import {
  AutoAwesome as AIIcon,
  Visibility as PublishIcon,
  VisibilityOff as DraftIcon,
  CheckCircle as ApproveIcon,
  History as HistoryIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { Client, resumesApi, clientsApi, publishApi, promptTemplatesApi, PromptTemplate } from '../../services/api'
import AttachmentsTab from './AttachmentsTab'
import UploadedResumePanel from './UploadedResumePanel'
import PublishHistoryDrawer from './PublishHistoryDrawer'
import { useNotification } from '../../hooks/useNotification'
import { useWebSocket } from '../../hooks/useWebSocket'
import { userAtom } from '../../state/auth'
import { isResumeLive } from '../../utils/resumeFile'

interface ResumeTabProps {
  client: Client
  onUpdate: (client: Partial<Client>) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResumeData = string | Record<string, any>

// Convert resume content (string or object) to HTML for the editor
const resumeToHtml = (content: ResumeData): string => {
  if (!content) return ''

  // If it's already a string
  if (typeof content === 'string') {
    // If it already looks like HTML, return as-is
    if (content.startsWith('<')) return content
    // Try to parse as JSON
    try {
      const parsed = JSON.parse(content)
      return resumeToHtml(parsed)
    } catch {
      // Convert plain text to HTML paragraphs
      return content
        .split('\n\n')
        .map((paragraph) => {
          const lines = paragraph.split('\n').join('<br/>')
          return `<p>${lines}</p>`
        })
        .join('')
    }
  }

  // If it's an object (structured resume from AI), format it nicely
  const sections: string[] = []

  if (content.professionalSummary) {
    sections.push('<h2>Professional Summary</h2>')
    sections.push(`<p>${content.professionalSummary}</p>`)
  }

  if (content.skills?.length) {
    sections.push('<h2>Key Skills</h2>')
    sections.push('<ul>')
    content.skills.forEach((skill: string) => {
      sections.push(`<li>${skill}</li>`)
    })
    sections.push('</ul>')
  }

  if (content.experience?.length) {
    sections.push('<h2>Experience</h2>')
    content.experience.forEach(
      (exp: { title: string; company: string; duration?: string; bullets?: string[] }) => {
        sections.push(`<h3>${exp.title} - ${exp.company}</h3>`)
        if (exp.duration) {
          sections.push(`<p><em>${exp.duration}</em></p>`)
        }
        if (exp.bullets?.length) {
          sections.push('<ul>')
          exp.bullets.forEach((bullet: string) => {
            sections.push(`<li>${bullet}</li>`)
          })
          sections.push('</ul>')
        }
      }
    )
  }

  if (content.additionalSections) {
    Object.entries(content.additionalSections).forEach(([sectionName, sectionContent]) => {
      sections.push(`<h2>${sectionName}</h2>`)
      if (typeof sectionContent === 'string') {
        sections.push(`<p>${sectionContent}</p>`)
      } else if (Array.isArray(sectionContent)) {
        sections.push('<ul>')
        sectionContent.forEach((item: string) => {
          sections.push(`<li>${item}</li>`)
        })
        sections.push('</ul>')
      }
    })
  }

  return sections.join('')
}

function formatRole(role: string): string {
  const roleMap: Record<string, string> = {
    'admin': 'Admin',
    'client_success': 'Client Success',
    'resume_writer': 'Resume Writer',
    'sales': 'Sales',
    'coach': 'Coach',
  }
  return roleMap[role] || 'Staff'
}

export default function ResumeTab({ client, onUpdate }: ResumeTabProps) {
  const [user] = useAtom(userAtom)
  const [generating, setGenerating] = useState(false)
  const [editedContent, setEditedContent] = useState<string>('')
  const [feedback, setFeedback] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [switchingSource, setSwitchingSource] = useState(false)
  const [internalApproving, setInternalApproving] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [templates, setTemplates] = useState<PromptTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  const { showSuccess, showError } = useNotification()
  const clientIdRef = useRef<string | null>(null)

  // Check if user is staff (not a client)
  const isStaff = user?.role !== 'client'

  // The resume is either the one the AI generates or a file staff upload. The two
  // share one publish and approval lifecycle; only what there is to publish differs.
  const isUploaded = client.resumeSource === 'uploaded'
  const hasResumeToPublish = isUploaded ? !!client.resumeAttachmentId : !!client.resumeContent
  const sourceLocked = isResumeLive(client)

  // Keep track of current client ID for WebSocket handler
  useEffect(() => {
    clientIdRef.current = client?.id || null
  }, [client?.id])

  // WebSocket handlers for async resume generation
  const wsHandlers = useMemo(
    () => ({
      resume_generated: (message: { action: string; data: unknown }) => {
        const data = message.data as { clientId?: string; resumeContent?: ResumeData }
        if (data?.clientId === clientIdRef.current) {
          const receivedContent = data.resumeContent
          if (receivedContent) {
            setEditedContent(resumeToHtml(receivedContent))
            setGenerating(false)
            showSuccess('Resume content generated successfully')
            onUpdate({ resumeContent: receivedContent, resumeIsDraft: true })
          }
        }
      },
      resume_generation_failed: (message: { action: string; data: unknown }) => {
        const data = message.data as { clientId?: string; error?: string }
        if (data?.clientId === clientIdRef.current) {
          setGenerating(false)
          showError(data.error || 'Resume generation did not complete. Try again.')
        }
      },
    }),
    [showSuccess, showError, onUpdate]
  )

  // Connect to WebSocket with handlers
  useWebSocket(wsHandlers)

  // Reset state when client changes
  useEffect(() => {
    if (client) {
      setGenerating(false)
      setFeedback('')
      // Load existing content if available
      if (client.resumeContent) {
        setEditedContent(resumeToHtml(client.resumeContent))
      } else {
        setEditedContent('')
      }
    }
  }, [client?.id])

  // Fetch resume templates on mount
  useEffect(() => {
    promptTemplatesApi.list('resume')
      .then((data) => {
        setTemplates(data)
        const defaultTemplate = data.find((t) => t.isDefault)
        if (defaultTemplate) setSelectedTemplateId(defaultTemplate.id)
      })
      .catch(() => {
        // Templates are optional — silently fail and show just System Default
      })
  }, [])

  // Quill editor modules configuration
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
      ],
    }),
    []
  )

  // Check if prerequisites are met
  const hasStrengths = !!client.cliftonStrengthsCompletedAt || (client.cliftonStrengths && client.cliftonStrengths.length >= 5)
  const hasDreamJob = !!client.dreamJobCompletedAt
  const canGenerate = hasStrengths || hasDreamJob

  const existingContent = client.resumeContent
  const htmlContent = existingContent ? resumeToHtml(existingContent) : ''

  const handleGenerateResume = async () => {
    try {
      setGenerating(true)
      // New generation = draft — update UI immediately so buttons show "Publish"
      onUpdate({ resumeIsDraft: true, internalResumeApproved: false, internalApprovedBy: undefined, internalApprovedAt: undefined })
      const result = await resumesApi.generate(client.id, feedback || undefined, selectedTemplateId || undefined)

      // Check if the response indicates async processing
      if (result.status === 'generating') {
        // Resume is being generated asynchronously, WebSocket will notify when done
        showSuccess('Generating resume content... This may take a moment.')
        return
      }

      // If content was returned directly (shouldn't happen with async, but handle it)
      if (result.resumeContent) {
        setEditedContent(resumeToHtml(result.resumeContent))
        showSuccess(
          feedback ? 'Resume improved successfully' : 'Resume content generated successfully'
        )
        onUpdate({ resumeContent: result.resumeContent, resumeIsDraft: true })
        setGenerating(false)
        setFeedback('')
      }
    } catch (error) {
      console.error('Error generating resume:', error)
      showError('Resume generation did not complete. Try again.')
      setGenerating(false)
    }
  }

  const handleSaveResume = async () => {
    try {
      setSaving(true)
      // Save the edited HTML content directly to the client record (always as draft)
      await clientsApi.update({ id: client.id, resumeContent: editedContent, resumeIsDraft: true })
      showSuccess('Resume saved as draft')
      onUpdate({ resumeContent: editedContent, resumeIsDraft: true })
    } catch (error) {
      console.error('Error saving resume:', error)
      showError('Failed to save resume')
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async (mode: 'draft' | 'ready') => {
    setPublishing(true)
    try {
      const result = await publishApi.publishResume(client.id, mode)
      onUpdate({
        resumeIsDraft: false,
        resumePublishMode: result.resumePublishMode,
        resumeApproved: false,
        internalResumeApproved: false,
        internalApprovedBy: undefined,
        internalApprovedAt: undefined,
      })
      showSuccess(mode === 'draft' ? 'Resume published as draft' : 'Resume published to client')
    } catch (error) {
      console.error('Failed to publish resume:', error)
      showError('Failed to publish resume')
    } finally {
      setPublishing(false)
    }
  }

  const handleUnpublish = async () => {
    setPublishing(true)
    try {
      await publishApi.unpublishResume(client.id)
      onUpdate({
        resumeIsDraft: true,
        resumeApproved: false,
        internalResumeApproved: false,
        internalApprovedBy: undefined,
        internalApprovedAt: undefined,
      })
      showSuccess('Resume hidden from client')
    } catch (error) {
      console.error('Failed to unpublish resume:', error)
      showError('Failed to update resume visibility')
    } finally {
      setPublishing(false)
    }
  }

  const handleSourceChange = async (_: React.MouseEvent<HTMLElement>, next: 'ai' | 'uploaded' | null) => {
    if (!next || next === (client.resumeSource ?? 'ai')) return
    setSwitchingSource(true)
    try {
      const result = await publishApi.setResumeSource(client.id, next)
      onUpdate({
        resumeSource: result.resumeSource,
        resumeAttachmentId: undefined,
        resumeIsDraft: true,
        resumeStatus: result.resumeStatus,
      })
    } catch (error) {
      console.error('Failed to change the resume source:', error)
      showError(error instanceof Error ? error.message : 'Failed to change the resume source')
    } finally {
      setSwitchingSource(false)
    }
  }

  const handleInternalApprove = async () => {
    setInternalApproving(true)
    try {
      const result = await publishApi.internalApproveResume(client.id)
      onUpdate({
        internalResumeApproved: result.internalResumeApproved,
        internalApprovedBy: user?.email || 'staff',
        internalApprovedAt: new Date().toISOString(),
      })
      showSuccess('Resume internally approved')
    } catch (error) {
      console.error('Failed to approve resume:', error)
      showError('Failed to approve resume')
    } finally {
      setInternalApproving(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Title and publish controls are both as wide as their content, and the
          record column is narrower than the two of them from about 800px down,
          so the row has to wrap rather than run off the side of the column. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">{isUploaded ? 'Uploaded Resume' : 'AI Resume Generator'}</Typography>
          {isStaff && hasResumeToPublish && (() => {
            const isPublished = !client.resumeIsDraft
            const publishMode = client.resumePublishMode ?? 'ready'
            if (!isPublished) return (
              <Chip icon={<DraftIcon sx={{ fontSize: 14 }} />} label="Unpublished" size="small" color="warning" sx={{ height: 22, fontSize: '0.75rem' }} />
            )
            if (publishMode === 'draft') return (
              <Chip icon={<DraftIcon sx={{ fontSize: 14 }} />} label="Draft" size="small" sx={{ height: 22, fontSize: '0.75rem', bgcolor: 'warning.light', color: 'warning.contrastText' }} />
            )
            return (
              <Chip icon={<PublishIcon sx={{ fontSize: 14 }} />} label="Published" size="small" color="success" sx={{ height: 22, fontSize: '0.75rem' }} />
            )
          })()}
        </Box>
        {isStaff && hasResumeToPublish && (() => {
          const isPublished = !client.resumeIsDraft
          const publishMode = client.resumePublishMode ?? 'ready'
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              {/* When unpublished: Publish as Ready + Publish as Draft */}
              {!isPublished && (
                <>
                  <Tooltip title="Publish to client as final">
                    <Button variant="outlined" size="small" startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />} onClick={() => handlePublish('ready')} disabled={publishing || saving || generating} color="success">
                      Publish &amp; Notify Client
                    </Button>
                  </Tooltip>
                  <Tooltip title="Share with client as work-in-progress">
                    <Button variant="outlined" size="small" startIcon={publishing ? <CircularProgress size={16} /> : <DraftIcon />} onClick={() => handlePublish('draft')} disabled={publishing || saving || generating} color="warning">
                      Publish as Draft
                    </Button>
                  </Tooltip>
                </>
              )}
              {/* When published as draft: Publish as Ready + Unpublish */}
              {isPublished && publishMode === 'draft' && (
                <>
                  <Tooltip title="Upgrade to final — client can approve">
                    <Button variant="outlined" size="small" startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />} onClick={() => handlePublish('ready')} disabled={publishing || saving || generating} color="success">
                      Publish &amp; Notify Client
                    </Button>
                  </Tooltip>
                  <Tooltip title="Hide from client">
                    <Button variant="outlined" size="small" startIcon={publishing ? <CircularProgress size={16} /> : <DraftIcon />} onClick={handleUnpublish} disabled={publishing || saving || generating} color="inherit">
                      Unpublish
                    </Button>
                  </Tooltip>
                </>
              )}
              {/* When published as ready: Unpublish */}
              {isPublished && publishMode === 'ready' && (
                <Tooltip title="Hide from client">
                  <Button variant="outlined" size="small" startIcon={publishing ? <CircularProgress size={16} /> : <DraftIcon />} onClick={handleUnpublish} disabled={publishing || saving || generating} color="inherit">
                    Unpublish
                  </Button>
                </Tooltip>
              )}
              {/* Internal approve — only when published as ready */}
              {isPublished && publishMode === 'ready' && !client.internalResumeApproved && (
                <Tooltip title="Approve resume internally before client review">
                  <Button variant="outlined" size="small" startIcon={internalApproving ? <CircularProgress size={16} /> : <ApproveIcon />} onClick={handleInternalApprove} disabled={internalApproving || saving || generating} color="primary">
                    Internal Approve
                  </Button>
                </Tooltip>
              )}
              {isPublished && publishMode === 'ready' && client.internalResumeApproved && (
                <Chip
                  icon={<ApproveIcon />}
                  label={`Approved by ${client.internalApprovedByRole ? formatRole(client.internalApprovedByRole) : 'Staff'}`}
                  size="small"
                  color="success"
                  sx={{ height: 28 }}
                />
              )}
              <Tooltip title="Publish history">
                <IconButton size="small" onClick={() => setHistoryOpen(true)}>
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
              {!isUploaded && (
                <Tooltip title="Preview as client sees it">
                  <IconButton size="small" onClick={() => setPreviewOpen(true)}>
                    <PreviewIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )
        })()}
      </Box>

      {isStaff && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Resume source
          </Typography>
          <Tooltip title={sourceLocked ? 'Unpublish the resume to change its source' : ''}>
            <span>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={isUploaded ? 'uploaded' : 'ai'}
                onChange={handleSourceChange}
                disabled={sourceLocked || switchingSource || generating || saving}
                aria-label="Resume source"
              >
                <ToggleButton value="ai">AI-generated</ToggleButton>
                <ToggleButton value="uploaded">Uploaded file</ToggleButton>
              </ToggleButtonGroup>
            </span>
          </Tooltip>
        </Box>
      )}

      {isUploaded ? (
        <UploadedResumePanel client={client} onUpdate={onUpdate} />
      ) : (
        <>
          {!canGenerate && (
            <Alert severity="info">
              To generate a resume, please complete at least one of the following:
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li>At least 5 Clifton Strengths</li>
                <li>Dream Job form</li>
              </ul>
            </Alert>
          )}

          {generating && (
            <Alert severity="info">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">
                  Building your resume. This takes about 30 to 60 seconds.
                </Typography>
              </Box>
            </Alert>
          )}

          {/* Feedback input for regeneration */}
          {(editedContent || htmlContent) && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Feedback for Regeneration (optional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Enter feedback to improve the resume (e.g., 'Focus more on leadership experience', 'Make bullets more quantitative')"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                disabled={generating || saving}
                size="small"
              />
            </Box>
          )}

          {/* Template selector */}
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="resume-template-label">Resume Template</InputLabel>
            <Select
              labelId="resume-template-label"
              value={selectedTemplateId}
              label="Resume Template"
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              disabled={generating || saving}
            >
              <MenuItem value="">System Default</MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                  {t.isDefault ? ' (Default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={generating ? <CircularProgress size={16} /> : <AIIcon />}
              onClick={handleGenerateResume}
              disabled={generating || saving || !canGenerate}
            >
              {generating
                ? 'Generating...'
                : editedContent || htmlContent
                  ? feedback
                    ? 'Improve with Feedback'
                    : 'Regenerate'
                  : 'Generate Resume'}
            </Button>
            {(editedContent || htmlContent) && (
              <Button
                variant="contained"
                onClick={handleSaveResume}
                disabled={saving || generating}
              >
                {saving ? 'Saving...' : 'Save Resume'}
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          {(editedContent || htmlContent) ? (
            <Box
              sx={{
                '& .ql-container': {
                  minHeight: 400,
                  maxHeight: 600,
                  overflow: 'auto',
                  fontSize: '14px',
                },
                '& .ql-editor': {
                  minHeight: 400,
                },
              }}
            >
              <ReactQuill
                theme="snow"
                value={editedContent || htmlContent}
                onChange={setEditedContent}
                modules={modules}
                placeholder="Resume content will appear here..."
              />
            </Box>
          ) : (
            <Box
              sx={{
                minHeight: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No resume content generated yet. Click "Generate Resume" to create AI-powered resume
                content.
              </Typography>
            </Box>
          )}
        </>
      )}

      <PublishHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        clientId={client.id}
      />

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Client Portal Preview</Typography>
          <IconButton size="small" onClick={() => setPreviewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            This is how the client sees the resume in the portal.
          </Alert>
          {(client.resumePublishMode ?? 'ready') === 'draft' && !client.resumeIsDraft && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Your strategist is still working on this. Do not use it for applications yet.
              </Typography>
            </Alert>
          )}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              backgroundColor: 'background.paper',
              '& h2': { fontSize: '1.25rem', fontWeight: 600, color: 'primary.main', mt: 3, mb: 1 },
              '& h3': { fontSize: '1.1rem', fontWeight: 500, mt: 2, mb: 0.5 },
              '& p': { mb: 1, lineHeight: 1.6 },
              '& ul': { pl: 2, mb: 1 },
              '& li': { mb: 0.5 },
            }}
          >
            {/* Read the in-memory editor buffer (editedContent) first so the
                preview shows live unsaved edits, falling back to the
                persisted DB content. Mirrors the Quill editor's value binding
                a few hundred lines up so staff see exactly what they're
                editing — not the last-saved version. */}
            <div dangerouslySetInnerHTML={{ __html: editedContent || htmlContent }} />
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 3 }} />
      {/* The list loads once, so it is remounted when the designated file changes: a
          replaced or removed resume file must not linger in it. */}
      <AttachmentsTab
        key={client.resumeAttachmentId ?? 'no-resume-file'}
        entityId={client.id}
        entityType="resume_document"
        title="Uploaded Documents"
        emptyStateText="Upload resumes, cover letters, executive bios, and other documents to share with this client"
        client={client}
        hideIds={client.resumeAttachmentId ? [client.resumeAttachmentId] : undefined}
      />
    </Box>
  )
}
